/**
 * Downscale an image blob so its longest edge is at most maxDim pixels and
 * re-encode it as JPEG. Album covers render as small thumbnails (≤192px), so
 * shrinking the multi-megabyte originals keeps cached blobs tiny and fast to
 * decode on every load.
 *
 * Returns the original blob unchanged if it is already small enough, or if
 * canvas decoding is unavailable (graceful fallback).
 */
export async function downscaleImageBlob(
  blob: Blob,
  maxDim = 512,
  quality = 0.85
): Promise<Blob> {
  if (typeof createImageBitmap !== 'function' || typeof document === 'undefined') {
    return blob;
  }

  try {
    const bitmap = await createImageBitmap(blob);
    const { width, height } = bitmap;
    const longest = Math.max(width, height);

    if (longest <= maxDim) {
      bitmap.close?.();
      return blob;
    }

    const scale = maxDim / longest;
    const w = Math.round(width * scale);
    const h = Math.round(height * scale);

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      bitmap.close?.();
      return blob;
    }
    ctx.drawImage(bitmap, 0, 0, w, h);
    bitmap.close?.();

    const out = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob((b) => resolve(b), 'image/jpeg', quality)
    );
    return out ?? blob;
  } catch {
    return blob;
  }
}
